<script setup lang="ts">
import { computed } from 'vue'
import type { InputDate } from '../types'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useCardContext } from '../composables/useCardContext'
import { spacingClasses } from '../utils/classes'

const props = defineProps<{ element: InputDate }>()

const { inputs, errors, cardInstanceId, isEnabled } = useCardContext()
const inputReadOnly = computed(() => !isEnabled.value || props.element.isEnabled === false)

function handleInput(value: string | number) {
  inputs[props.element.id] = String(value ?? '')
  if (errors[props.element.id]) {
    errors[props.element.id] = ''
  }
}

function normalizeDomToken(value: string): string {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_')
}

const fieldInputId = computed(() => `${cardInstanceId}-${normalizeDomToken(props.element.id)}`)
</script>

<template>
  <div :class="cn('flex flex-col gap-1.5', spacingClasses[props.element.spacing ?? 'default'])">
    <Label v-if="props.element.label" :for="fieldInputId">
      {{ props.element.label }}
      <span v-if="props.element.isRequired" class="text-destructive">*</span>
    </Label>
    <Input
      :id="fieldInputId"
      type="date"
      :placeholder="props.element.placeholder"
      :min="props.element.min"
      :max="props.element.max"
      :disabled="inputReadOnly"
      :model-value="inputs[props.element.id] ?? props.element.value ?? ''"
      :class="cn('bg-background', errors[props.element.id] && 'border-destructive')"
      @update:model-value="handleInput"
    />
    <p v-if="errors[props.element.id]" class="text-sm text-destructive">
      {{ errors[props.element.id] }}
    </p>
  </div>
</template>
